import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form } from '@unform/web';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { FormHandles } from '@unform/core';

import api from 'services/api';
import { Avaliacaoeng, Role } from '../../utils/variables';
import { show } from 'store/modules/dialog/actions';
import { AvaliacaoEng, OptativoLocal as OptativoLocalItem } from 'pages/@types';
import NotFoundImage from 'assets/Fill out-pana.svg';
import NotFound from 'components/NotFound';
import AppError from 'utils/AppError';
import getValidationErrors from 'services/getValidationErros';
import { useModal } from 'hooks/modal';

// componentes
import Button from 'components/Button';
import Card from 'components/Card';
import Pagination from 'components/Pagination';
import Input from 'components/Input';
import PlayerBaixa from 'components/PlayerBaixa';
import Loader from 'components/ContentLoader/GenericLoader';
import TextArea from 'components/TextArea';
import Spinner from 'components/Spinner';

// estilos
import {
  Container,
  Lista,
  Item,
  Dados,
  Steps,
  Step,
  Acoes,
  AvaliacaoModal,
  Alert,
  ObservacoesModal,
} from './styles';
import { Scroll } from 'styles/global';

// icons
import {
  FiSearch,
  FiPlus,
  FiXCircle,
  FiClock,
  FiBox,
  FiCheckCircle,
  FiTrash2,
  FiTv,
  FiAlertTriangle,
} from 'react-icons/fi';
import Tooltip from 'components/Tooltip';
import { Chamada } from 'pages/@types';
import { format } from 'date-fns';
import { usePermit } from 'utils/hooks/usePermit';
import Flag from 'components/Flag';

interface IStep {
  key: number;
  step: number;
  flag: string;
  titulo: string;
  status: {
    flag: 'finish' | 'wait' | 'error' | 'process';
    mensagem: string;
  };
}

export interface IStepPromo {
  curstate: number;
  steps: IStep[];
}
interface IPromo extends Chamada {
  previa: boolean;
  state: IStepPromo;
}

const schema = Yup.object().shape({
  obseng: Yup.string().required('Campo Obrigatório'),
});

export default function OptativoLocal() {
  const navigate = useNavigate();
  /**
   * STATES
   */
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [optativosLocais, setOptativosLocais] = useState<OptativoLocalItem[]>(
    [],
  );
  const [player, setPlayer] = useState({
    open: false,
    midia_url: '',
    pipe: false,
  });

  const formRefAvaliacao = useRef<FormHandles>(null);
  const avaliacaoEngRef = useRef('');
  const selectedId = useRef(0);

  const { showModal, hideModal } = useModal();
  /**
   * REDUX
   */
  const { profile } = useSelector((state: any) => state.usuario);
  const dispatch = useDispatch();
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  const hasOptativosLocaisEdit = usePermit([Role.PROMO, Role.JORN], []);

  const handleSubmitAvaliacaoEng = useCallback(
    async (data, id) => {
      if (formRefAvaliacao.current) {
        setLoadingSubmit(true);
        try {
          formRefAvaliacao.current.setErrors({});
          const avaliacaoeng = avaliacaoEngRef.current as AvaliacaoEng;
          if (
            avaliacaoeng === Avaliacaoeng.RESSALVA ||
            avaliacaoeng === Avaliacaoeng.REPROVADO
          ) {
            await schema.validate(data, { abortEarly: false });
          }

          const response = await api.patch(`/optativolocal/${id}`, {
            obseng: data.obseng,
            avaliacaoeng,
          });
          if (response.data.optativolocal) {
            toast.success('Optativo avaliado com sucesso');
            const optativoUpdated = response.data.optativolocal;
            const isReprovado =
              optativoUpdated.avaliacaoeng === Avaliacaoeng.REPROVADO;
            const isRessalva =
              optativoUpdated.avaliacaoeng === Avaliacaoeng.RESSALVA;

            setOptativosLocais(prev =>
              prev.map(o => {
                if (o.id !== id) return o;

                return {
                  ...o,
                  avaliacaoeng: optativoUpdated.avaliacaoeng,
                  avaliador: optativoUpdated.avaliador,
                  checked_at: optativoUpdated.checked_at,
                  state: {
                    ...o.state,
                    curstate: 6,
                    steps: o.state.steps.map(step => {
                      // Step 6 - Avaliação
                      if (step.step === 6) {
                        return {
                          ...step,
                          status: {
                            ...step.status,
                            mensagem: isReprovado
                              ? 'Mídia reprovada no Exibidor'
                              : isRessalva
                              ? 'Mídia aprovada com ressalva no Exibidor'
                              : 'Mídia aprovada no Exibidor',
                            flag: isReprovado ? 'error' : 'finish',
                          },
                        };
                      }

                      // Step 7 - Conferência
                      if (step.step === 7 && !isReprovado) {
                        return {
                          ...step,
                          status: {
                            ...step.status,
                            flag: 'process',
                          },
                        };
                      }

                      return step;
                    }),
                  },
                };
              }),
            );
            hideModal();
          } else
            toast.error('Falha de comunicação com o sistema', {
              toastId: 'API Offline',
            });
        } catch (error) {
          if (error instanceof Yup.ValidationError) {
            const errors = getValidationErrors(error);
            formRefAvaliacao.current.setErrors(errors);
            return;
          }
          if (error instanceof Error) {
            toast.error(error.message);
          }
        } finally {
          setLoadingSubmit(false);
        }
      }
    },
    [hideModal],
  );

  const renderAvaliacaoEng = useCallback(
    optativo => {
      return (
        <AvaliacaoModal>
          <Form
            ref={formRefAvaliacao}
            onSubmit={data => handleSubmitAvaliacaoEng(data, optativo.id)}
          >
            <TextArea
              name="obseng"
              placeholder="Digite aqui a observação"
              height="100"
            />
            <div>
              <Button
                onClick={() => {
                  avaliacaoEngRef.current = Avaliacaoeng.REPROVADO;
                }}
                type="submit"
                color="danger"
                disabled={
                  loadingSubmit ||
                  optativo.avaliacaoeng === Avaliacaoeng.REPROVADO
                }
              >
                {!loadingSubmit ? 'REPROVAR' : <Spinner type="ThreeDots" />}
              </Button>
              <Button
                onClick={() => {
                  avaliacaoEngRef.current = Avaliacaoeng.RESSALVA;
                }}
                type="submit"
                color="warn"
                disabled={
                  loadingSubmit ||
                  optativo.avaliacaoeng === Avaliacaoeng.RESSALVA
                }
              >
                {!loadingSubmit ? 'RESSALVA' : <Spinner type="ThreeDots" />}
              </Button>
              <Button
                onClick={() => {
                  avaliacaoEngRef.current = Avaliacaoeng.APROVADO;
                }}
                type="submit"
                color="success"
                disabled={
                  loadingSubmit ||
                  optativo.avaliacaoeng === Avaliacaoeng.APROVADO
                }
              >
                {!loadingSubmit ? 'APROVAR' : <Spinner type="ThreeDots" />}
              </Button>
            </div>
          </Form>
        </AvaliacaoModal>
      );
    },
    [handleSubmitAvaliacaoEng, loadingSubmit],
  );

  const handleAvaliacaoEng = useCallback(
    optativo => {
      showModal({
        title: optativo.titulo,
        component: renderAvaliacaoEng(optativo),
        width: '40vw',
      });
    },
    [renderAvaliacaoEng, showModal],
  );

  const handleObservacoes = useCallback(
    optativo => {
      showModal({
        title: `OBSERVAÇÕES DE: ${optativo.titulo}`,
        component: (
          <ObservacoesModal>
            {optativo.obseditor && (
              <section>
                <h4>Observações do Editor</h4>
                <p>{optativo.obseditor}</p>
                <footer>Editado por: {optativo.usuario.nome}</footer>
              </section>
            )}
            {optativo.midia?.obseng && (
              <section>
                <h4>Observações Técnicas na avaliação no ADX</h4>
                <p>{optativo.midia.obseng}</p>
                <footer>Avaliador por: {optativo.midia.avaliador.nome}</footer>
              </section>
            )}
            {optativo.obseng && (
              <section>
                <h4>Observações Técnicas na avaliação no Exibidor</h4>
                <p>{optativo.obseng}</p>
                <footer>Avaliador por: {optativo.avaliador.nome}</footer>
              </section>
            )}
          </ObservacoesModal>
        ),
        width: '40vw',
      });
    },
    [showModal],
  );

  const handleDelete = useCallback(
    optativo => {
      selectedId.current = optativo.id;
      const optionBase = {
        type: 'question',
        contentHTML: `Remover <b>${optativo.titulo}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/optativolocal/${optativo.id}`,
          method: api.delete,
          action: 'delete',
        },
        haveCallback: true,
        successMessage: 'Optativo Local removido!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  const handleConferencia = useCallback(
    optativo => {
      selectedId.current = optativo.id;
      const optionBase = {
        type: 'question',
        contentHTML: `Confirmar conferência de <b>${optativo.titulo}</b> ?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/optativolocal/${optativo.id}`,
          method: api.patch,
          data: { conferencia: true },
          action: 'conferencia',
        },
        haveCallback: true,
        successMessage: 'Optativo Local conferido!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );
  const handleStepClick = useCallback(
    (optativo, status) => {
      const curState = optativo.state.curstate;
      if (!optativo.midia) return;
      if (curState === 2 && status === 'process') {
        navigate(`/closedcaptioneditor/${optativo.midia.id}/optativos`);
      } else if (curState === 3 && status === 'process') {
        navigate('/player');
      } else if (curState === 5) {
        handleAvaliacaoEng(optativo);
      } else if (curState === 6 && status === 'process') {
        handleConferencia(optativo);
      }
    },
    [handleAvaliacaoEng, handleConferencia, navigate],
  );

  const handlePlayer = useCallback(
    midia => {
      if (!!midia && midia.preview_url) {
        setPlayer({
          open: !player.open,
          midia_url: midia.preview_url,
          pipe: false,
        });
      } else {
        dispatch(
          show({
            type: 'warning',
            text: 'Preview indisponível no momento',
            showConfirmButton: false,
          }),
        );
      }
    },
    [dispatch, player.open],
  );

  // fecha player
  const closeVideo = useCallback(() => {
    setPlayer({
      open: !player.open,
      midia_url: '',
      pipe: false,
    });
  }, [player.open]);

  /**
   * USE EFFECT
   */
  useEffect(() => {
    if (callbackDialog && callbackDialog.success) {
      if (callbackDialog.success.action === 'delete' && selectedId.current) {
        setOptativosLocais(prevState =>
          prevState.map(s =>
            s.id !== selectedId.current
              ? s
              : { ...s, deleted_at: new Date().toString() },
          ),
        );
      }
      if (
        callbackDialog.success.action === 'conferencia' &&
        callbackDialog.success.data.optativolocal?.usuarioconferencia &&
        selectedId.current
      ) {
        setOptativosLocais(prevState =>
          prevState.map(s =>
            s.id !== selectedId.current
              ? s
              : {
                  ...s,
                  state: {
                    curstate: 7,
                    steps: s.state.steps.map((step, index) =>
                      index !== 6
                        ? step
                        : {
                            ...step,
                            status: {
                              ...step.status,
                              flag: 'finish', // change what you need
                              mensagem: `Mídia conferida no Exibidor em ${format(
                                new Date(),
                                'dd/MM/yyyy HH:mm',
                              )} por ${
                                callbackDialog.success.data.optativolocal
                                  .usuarioconferencia.nome
                              }`,
                            },
                          },
                    ),
                  },
                },
          ),
        );
      }
    }
  }, [callbackDialog, selectedId]);

  useEffect(() => {
    const controller = new AbortController();
    async function loadOptativoLocal() {
      setLoading(true);
      try {
        const response = await api.get('/optativolocal', {
          signal: controller.signal,
          params: { page, search: filtro },
        });
        if (response && response.data) {
          setOptativosLocais(response.data.optativoslocais);
          setPages(response.data.pages);
        } else
          toast.error('Falha de comunicação com o sistema', {
            toastId: 'API Offline',
          });
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoading(false);
      }
    }
    loadOptativoLocal();
    return () => {
      controller.abort();
    };
  }, [filtro, page]);

  return (
    <Container>
      <header>
        <Button
          color="primary"
          onClick={() => navigate('/optativoslocais/novo')}
          disabled={!hasOptativosLocaisEdit}
        >
          NOVO OPTATIVO LOCAL <FiPlus />
        </Button>
      </header>
      <Card>
        <header>
          <h4>LISTA DE OPTATIVOS LOCAIS</h4>
          <Pagination
            total={pages}
            activePage={page}
            onClick={page => setPage(page)}
          />
          <div>
            <Form
              onSubmit={data => {
                setPage(1);
                setFiltro(data.search);
              }}
            >
              <Input
                name="search"
                placeholder="Buscar na Tabela"
                icon={<FiSearch />}
              />
            </Form>
          </div>
        </header>
        <Scroll>
          {loading ? (
            <Loader />
          ) : !optativosLocais ? (
            <NotFound img={NotFoundImage} title="Optativo Local">
              Nenhum Optativo Local encontrado
            </NotFound>
          ) : (
            <Lista>
              {optativosLocais.map(optativo => (
                <Item key={optativo.id}>
                  <Dados $disabled={!!optativo.deleted_at}>
                    <span>
                      <p>TITULO</p>
                      <p>{optativo.titulo}</p>
                    </span>
                    <span>
                      <p>PROGRAMA</p>
                      <p>{optativo.programa}</p>
                    </span>
                    <span>
                      <p>DURACAO</p>
                      <p>{optativo.duracao}</p>
                    </span>
                    <span>
                      <p>VERSÃO</p>
                      <Flag
                        type={optativo.versao}
                        name={optativo.versao.toUpperCase()}
                      />
                    </span>
                    <span>
                      <p>CRIADO EM</p>
                      <p>
                        {format(
                          new Date(optativo.created_at),
                          'dd/MM/yyyy HH:mm',
                        )}
                      </p>
                    </span>
                    {(optativo.obseng ||
                      optativo.obseditor ||
                      optativo.midia?.obseng) && (
                      <span>
                        <p>OBS.</p>
                        <Alert onClick={() => handleObservacoes(optativo)}>
                          <FiAlertTriangle />
                        </Alert>
                      </span>
                    )}
                  </Dados>
                  <Steps
                    key={optativo.id}
                    labelPlacement="vertical"
                    current={99}
                    icons={{
                      finish: <FiCheckCircle />,
                      error: <FiXCircle />,
                    }}
                  >
                    {optativo.state.steps.map((step, index) => {
                      const { flag, mensagem } = step.status;
                      const { curstate } = optativo.state;
                      const isDeleted = !!optativo.deleted_at;
                      const isProcess = flag === 'process';
                      const isError = flag === 'error';

                      const isRessalva =
                        optativo.avaliacaoeng === Avaliacaoeng.RESSALVA;

                      const isRessalvaMidia =
                        optativo.midia?.avaliacaoeng === Avaliacaoeng.RESSALVA;

                      const isStepAdx = step.step === 4;
                      const isStepAvaliacao = step.step === 6;

                      const showRessalvaIcon =
                        (isStepAdx && isRessalvaMidia) ||
                        (isStepAvaliacao && isRessalva);

                      const clickable =
                        (isProcess || isError) &&
                        ((curstate === 2 &&
                          isProcess &&
                          profile.role === Role.CEDOC) ||
                          ((curstate === 3 || curstate === 5) &&
                            profile.role === Role.ENG) ||
                          (curstate === 6 &&
                            isProcess &&
                            profile.role === Role.EXIB) ||
                          profile.role === Role.ADMIN);

                      const icon =
                        isDeleted || isError ? (
                          <FiXCircle />
                        ) : showRessalvaIcon ? (
                          <FiAlertTriangle />
                        ) : flag === 'process' ? (
                          <FiBox />
                        ) : flag === 'wait' ? (
                          <FiClock />
                        ) : (
                          <FiCheckCircle />
                        );

                      const stepId = `tooltip_step_${step.key}_${index}`;

                      return (
                        <React.Fragment
                          key={`${step.key}_fragment_${optativo.id}_${index}`}
                        >
                          <Step
                            disabled={isDeleted}
                            id={stepId}
                            title={step.titulo}
                            stepIndex={optativo.id}
                            status={flag}
                            next={optativo.state.steps[index + 1]?.status.flag}
                            icon={icon}
                            {...(clickable && {
                              onStepClick: () =>
                                handleStepClick(optativo, flag),
                            })}
                          />

                          <Tooltip target={stepId}>{mensagem}</Tooltip>
                        </React.Fragment>
                      );
                    })}
                  </Steps>

                  <Acoes>
                    <p>AÇÕES</p>
                    <Button
                      id={`id_preview_${optativo.id}`}
                      fill
                      color="font"
                      round
                      onClick={() => handlePlayer(optativo.midia)}
                      disabled={
                        !!optativo.deleted_at || !optativo.midia?.preview_url
                      }
                    >
                      <FiTv />
                    </Button>
                    <Tooltip
                      target={`id_preview_${optativo.id}`}
                      position="left"
                    >
                      ASSISTIR
                    </Tooltip>
                    <Button
                      id={`id_deletar${optativo.id}`}
                      fill
                      color="font"
                      round
                      onClick={() => handleDelete(optativo)}
                      disabled={
                        !!optativo.deleted_at || !hasOptativosLocaisEdit
                      }
                    >
                      <FiTrash2 />
                    </Button>
                    <Tooltip
                      target={`id_deletar${optativo.id}`}
                      position="left"
                    >
                      APAGAR
                    </Tooltip>
                  </Acoes>
                </Item>
              ))}
            </Lista>
          )}
        </Scroll>
      </Card>

      <PlayerBaixa
        open={player.open}
        toggleModal={() => closeVideo()}
        pipe={player.pipe}
        url={player.midia_url}
      />
    </Container>
  );
}
